import { useMemo, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import usePageMeta from '@/hooks/usePageMeta';
import Breadcrumbs from '@/components/base/Breadcrumbs';
import IncidentCard from '@/components/base/IncidentCard';
import { incidents } from '@/mocks/incidents';
import { topics } from '@/mocks/topics';

const riskOptions = [
  { value: 'all', label: '全部等级' },
  { value: 'high', label: '高风险' },
  { value: 'medium', label: '中风险' },
  { value: 'low', label: '低风险' },
];

export default function Incidents() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get('q') ?? '';
  const [draft, setDraft] = useState(keyword);
  const [category, setCategory] = useState('all');
  const [risk, setRisk] = useState('all');

  usePageMeta({
    title: '事件库｜网站数据泄露、漏洞与盗刷事件全收录 - 黑料雷达',
    description:
      '按关键词、风险分类与风险等级检索网站安全事件，包含数据泄露、安全漏洞、盗刷诈骗、隐私风险、违规收集与监管处罚记录的整理与解读。',
    keywords: '网站黑料事件库,数据泄露案例,安全漏洞通报,盗刷事件,隐私风险案例',
    canonical: '/incidents',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: '网站安全事件库',
      url: 'https://worswgu.cn/incidents',
      inLanguage: 'zh-CN',
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://worswgu.cn/' },
          { '@type': 'ListItem', position: 2, name: '事件库', item: 'https://worswgu.cn/incidents' },
        ],
      },
    },
  });

  const filtered = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    return incidents.filter((item) => {
      const matchQuery =
        !q ||
        item.title.toLowerCase().includes(q) ||
        item.summary.toLowerCase().includes(q) ||
        item.platform.toLowerCase().includes(q) ||
        item.tags.some((tag) => tag.toLowerCase().includes(q));
      const matchCategory = category === 'all' || item.category === category;
      const matchRisk = risk === 'all' || item.riskLevel === risk;
      return matchQuery && matchCategory && matchRisk;
    });
  }, [keyword, category, risk]);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const next = new URLSearchParams(searchParams);
    if (draft.trim()) next.set('q', draft.trim());
    else next.delete('q');
    setSearchParams(next);
  };

  const clearAll = () => {
    setDraft('');
    setCategory('all');
    setRisk('all');
    setSearchParams({});
  };

  return (
    <>
      <section className="border-b border-background-200 bg-background-100/70">
        <div className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
          <Breadcrumbs items={[{ label: '首页', to: '/' }, { label: '事件库' }]} />
          <h1 className="mt-5 font-heading text-2xl font-black text-foreground-950 md:text-4xl">
            网站安全事件库
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-7 text-foreground-600">
            这里汇集了我们持续整理的安全事件与监管通报。每条记录都尽量追溯来源，并区分「已确认」与「待核实」。
            用搜索与筛选快速定位你关心的风险类型。
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
        <form
          onSubmit={handleSubmit}
          className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5"
        >
          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3 py-2.5">
              <span className="flex h-4 w-4 items-center justify-center text-foreground-500">
                <i className="ri-search-line text-base"></i>
              </span>
              <input
                type="search"
                name="q"
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder="搜索事件标题、平台类型或标签关键词"
                aria-label="搜索事件"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-500"
              />
            </div>
            <button
              type="submit"
              className="whitespace-nowrap rounded-md bg-primary-600 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
            >
              搜索
            </button>
            <button
              type="button"
              onClick={clearAll}
              className="whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-background-100"
            >
              重置条件
            </button>
          </div>

          <div>
            <p className="text-xs font-medium text-foreground-500">风险分类</p>
            <div className="mt-2.5 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setCategory('all')}
                className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition-colors ${
                  category === 'all'
                    ? 'bg-foreground-900 text-background-50'
                    : 'border border-background-300 text-foreground-700 hover:bg-background-100'
                }`}
              >
                全部分类
              </button>
              {topics.map((topic) => (
                <button
                  key={topic.slug}
                  type="button"
                  onClick={() => setCategory(topic.slug)}
                  className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition-colors ${
                    category === topic.slug
                      ? 'bg-foreground-900 text-background-50'
                      : 'border border-background-300 text-foreground-700 hover:bg-background-100'
                  }`}
                >
                  {topic.name}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="text-xs font-medium text-foreground-500">风险等级</p>
            <div className="mt-2.5 flex flex-wrap gap-2">
              {riskOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setRisk(option.value)}
                  className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition-colors ${
                    risk === option.value
                      ? 'bg-primary-600 text-background-50'
                      : 'border border-background-300 text-foreground-700 hover:bg-background-100'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </form>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-2 text-sm text-foreground-600">
          <p>
            共找到 <strong className="text-foreground-900">{filtered.length}</strong> 条事件
            {keyword && (
              <>
                ，关键词「<strong className="text-primary-700">{keyword}</strong>」
              </>
            )}
          </p>
          <p className="text-xs text-foreground-400">数据更新至 2026 年 9 月</p>
        </div>

        {filtered.length > 0 ? (
          <div className="mt-4">
            {filtered.map((incident) => (
              <IncidentCard key={incident.id} incident={incident} layout="horizontal" />
            ))}
          </div>
        ) : (
          <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100/60 px-6 py-16 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-search-eye-line text-2xl"></i>
            </span>
            <p className="mt-4 font-heading text-lg font-bold text-foreground-900">没有匹配的事件</p>
            <p className="mt-2 max-w-md text-sm leading-6 text-foreground-600">
              试着减少筛选条件，或换一个更宽泛的关键词，比如「泄露」「扣款」「权限」。
            </p>
            <button
              type="button"
              onClick={clearAll}
              className="mt-5 whitespace-nowrap rounded-md bg-secondary-600 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-secondary-700"
            >
              清空全部条件
            </button>
          </div>
        )}
      </section>
    </>
  );
}